/**
 * Recarga automatica cuando el navegador pide un trozo de codigo
 * que ya no existe.
 *
 * Al desplegar, los ficheros cambian de nombre. Un navegador que
 * tenga el index viejo pedira un chunk que ya no esta y la pagina
 * se queda en blanco con "Failed to fetch dynamically imported
 * module".
 *
 * El ErrorBoundary lo cubria, pero el fallo ocurre dentro de un
 * Suspense con lazy() y no siempre llega hasta el. Aqui se escucha
 * a nivel de ventana, que si lo ve siempre.
 *
 * Se recarga como mucho una vez cada treinta segundos: si el chunk
 * falta de verdad, recargar en bucle no lo arregla y ademas impide
 * ver el error.
 */

const RELOAD_KEY = 'mw-chunk-reload'
const COOLDOWN_MS = 30_000

const PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Loading chunk',
  'Loading CSS chunk',
]

function isChunkError(message: unknown): boolean {
  if (typeof message !== 'string') return false
  return PATTERNS.some(p => message.includes(p))
}

function reloadOnce(): boolean {
  try {
    const last = sessionStorage.getItem(RELOAD_KEY)
    const now = Date.now()
    if (last && now - Number(last) < COOLDOWN_MS) return false
    sessionStorage.setItem(RELOAD_KEY, String(now))
  } catch {
    // Sin sessionStorage no se puede llevar la cuenta: se recarga igual,
    // que es mejor que dejar la pagina en blanco.
  }
  window.location.reload()
  return true
}

/** Engancha los escuchadores. Llamar una vez al arrancar la app. */
export function installChunkReloadGuard(): void {
  if (typeof window === 'undefined') return

  window.addEventListener('error', event => {
    if (isChunkError(event.message)) {
      event.preventDefault()
      reloadOnce()
    }
  })

  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason
    const message = reason instanceof Error ? reason.message : String(reason ?? '')
    if (isChunkError(message)) {
      event.preventDefault()
      reloadOnce()
    }
  })
}

export { isChunkError }
